import { useMemo } from 'react';
import {
    Area,
    Bar,
    CartesianGrid,
    ComposedChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';

import { getDateAggregationKey } from '../../../utils/getDateAggregationKey';
import { deriveQuantArcBacktestSeries, type QuantArcBacktestRow } from './backtestSeries';
import { WidgetWrapper } from './WidgetWrapper';
import { downsampleWithMarkers } from '../../../utils/performance';

interface Props {
    data: QuantArcBacktestRow[];
}

function formatNumber(value: unknown, digits = 2): string {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return '—';
    return numeric.toFixed(digits);
}

function CustomTooltip({ active, payload, label }: any) {
    if (!active || !payload?.length) return null;

    const pnlEntry = payload.find((p: any) => p.dataKey === 'position');
    if (!pnlEntry) return null;

    const formattedDate = getDateAggregationKey(label, 'datetime');

    return (
        <div style={{
            background: 'rgba(30, 30, 30, 0.95)',
            border: '1px solid var(--surface-glass-border)',
            borderRadius: '10px',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.5)',
            padding: '12px',
            fontSize: '0.85rem',
            color: 'var(--text-primary)',
            pointerEvents: 'none'
        }}>
            <div style={{ color: 'var(--text-muted)', marginBottom: '8px', fontWeight: '600', fontSize: '0.75rem' }}>
                {formattedDate}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
                <span style={{ opacity: 0.8 }}>Position:</span>
                <span style={{ color: 'var(--accent-warning)', fontWeight: 'bold' }}>
                    {formatNumber(pnlEntry.value, 0)}
                </span>
            </div>
        </div>
    );
}

export function QuantArcPositionWidget({ data }: Props) {
    const series = useMemo(() => {
        const raw = deriveQuantArcBacktestSeries(data);
        if (raw.length > 1000) {
            return downsampleWithMarkers(raw, 800, 'position', ['entryMarker', 'exitMarker', 'flipMarker']);
        }
        return raw;
    }, [data]);

    if (!series.length) {
        return (
            <WidgetWrapper title="💼 Position Timeline">
                <div style={{ color: 'var(--text-muted)', padding: '1rem' }}>No QuantArc position data available.</div>
            </WidgetWrapper>
        );
    }

    return (
        <WidgetWrapper title="💼 Position Timeline">
            <div style={{ width: '100%', height: '100%', padding: '0.5rem 0' }}>
                <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={series} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                        <defs>
                            <linearGradient id="colorPos" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="var(--accent-warning)" stopOpacity={0.3}/>
                                <stop offset="95%" stopColor="var(--accent-warning)" stopOpacity={0}/>
                            </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(255,255,255,0.05)" />
                        <XAxis 
                            dataKey="time" 
                            tickFormatter={(value) => getDateAggregationKey(value)} 
                            tick={{ fill: 'var(--text-muted)', fontSize: 10 }} 
                            axisLine={false}
                            tickLine={false}
                            minTickGap={30}
                        />
                        <YAxis 
                            yAxisId="position" 
                            tick={{ fill: 'var(--text-muted)', fontSize: 10 }} 
                            tickFormatter={(value) => formatNumber(value, 0)}
                            axisLine={false}
                            tickLine={false}
                            domain={['auto', 'auto']}
                        />
                        <Tooltip
                            content={<CustomTooltip />}
                            animationDuration={0}
                            cursor={{ stroke: 'rgba(255,255,255,0.2)', strokeWidth: 1 }}
                        />
                        <Bar
                            yAxisId="position"
                            dataKey="position"
                            name="position"
                            fill="var(--accent-warning)"
                            fillOpacity={0.1}
                            isAnimationActive={false}
                        />
                        <Area
                            yAxisId="position"
                            type="stepAfter"
                            dataKey="position"
                            name="position"
                            stroke="var(--accent-warning)"
                            strokeWidth={2}
                            fillOpacity={1}
                            fill="url(#colorPos)"
                            isAnimationActive={false}
                            activeDot={{ r: 4, strokeWidth: 0 }}
                        />
                    </ComposedChart>
                </ResponsiveContainer>
            </div>
        </WidgetWrapper>
    );
}
